/**
 * LiveSyncService - Cross-tab real-time synchronization using BroadcastChannel API
 * 
 * When a mutation (add/update/delete) happens in one tab, it broadcasts to all other
 * open tabs so they can refresh their data. This ensures consistency across:
 * - Inventory tab + POS tab open simultaneously
 * - Multiple cashier tabs
 * - Dashboard + any data entry tab
 * 
 * Also handles visibility-based refresh: when a tab becomes visible after being
 * hidden for a while, stale data is automatically refreshed.
 */

// Collections that can be synced across tabs
export type SyncCollection = 
  | 'products' 
  | 'categories' 
  | 'customers' 
  | 'suppliers' 
  | 'grns' 
  | 'expenses'
  | 'transactions'

export type SyncAction = 'add' | 'update' | 'delete' | 'refresh'

export interface SyncMessage {
  collection: SyncCollection
  action: SyncAction
  timestamp: number
  sourceTabId: string
}

type SyncHandler = (message: SyncMessage) => void

const CHANNEL_NAME = 'esales-pos-sync'
const STALE_THRESHOLD_MS = 30 * 1000 // 30 seconds - data older than this is considered stale

class LiveSyncService {
  private channel: BroadcastChannel | null = null
  private tabId: string
  private handlers: Map<SyncCollection, Set<SyncHandler>> = new Map()
  private globalHandlers: Set<SyncHandler> = new Set()
  private lastFetchTimestamps: Map<SyncCollection, number> = new Map()
  private isInitialized = false
  private visibilityHandler: (() => void) | null = null
  private onVisibilityRefresh: (() => void) | null = null

  constructor() {
    // Generate a unique tab identifier
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
  }

  /**
   * Initialize the sync service. Call once when the app boots.
   */
  init(onVisibilityRefresh?: () => void) {
    if (this.isInitialized) return

    // BroadcastChannel is supported in all modern browsers
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(CHANNEL_NAME)
        this.channel.onmessage = (event: MessageEvent<SyncMessage>) => {
          this.handleIncomingMessage(event.data)
        }
        console.log('[LiveSync] BroadcastChannel initialized:', this.tabId)
      } catch (e) {
        console.warn('[LiveSync] BroadcastChannel not available:', e)
      }
    } else {
      console.warn('[LiveSync] BroadcastChannel API not supported in this browser')
    }

    // Set up visibility-based refresh
    this.onVisibilityRefresh = onVisibilityRefresh || null
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.handleTabBecameVisible()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)

    this.isInitialized = true
  }

  /**
   * Clean up the sync service. Call when the app unmounts.
   */
  destroy() {
    if (this.channel) {
      this.channel.close()
      this.channel = null
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    this.handlers.clear()
    this.globalHandlers.clear()
    this.lastFetchTimestamps.clear()
    this.isInitialized = false
    console.log('[LiveSync] Destroyed')
  }

  /**
   * Broadcast a mutation to other tabs. Call this after a successful store mutation.
   * Other tabs will receive this and trigger a re-fetch of the affected collection.
   */
  broadcast(collection: SyncCollection, action: SyncAction) {
    const message: SyncMessage = {
      collection,
      action,
      timestamp: Date.now(),
      sourceTabId: this.tabId,
    }

    if (this.channel) {
      try {
        this.channel.postMessage(message)
      } catch (e) {
        console.warn('[LiveSync] Failed to broadcast:', e)
      }
    }

    // Update our own fetch timestamp (we already have the latest data)
    this.lastFetchTimestamps.set(collection, Date.now())
  }

  /**
   * Record that a collection was fetched (to track staleness).
   */
  markFetched(collection: SyncCollection) {
    this.lastFetchTimestamps.set(collection, Date.now())
  }

  /**
   * Check if a collection's data is stale (hasn't been fetched recently).
   */
  isStale(collection: SyncCollection): boolean {
    const lastFetch = this.lastFetchTimestamps.get(collection)
    if (!lastFetch) return true
    return (Date.now() - lastFetch) > STALE_THRESHOLD_MS
  }

  /**
   * Get all collections that are currently stale.
   */
  getStaleCollections(): SyncCollection[] {
    // products, customers, and batches are handled by Firestore real-time listeners — exclude from stale checks
    const allCollections: SyncCollection[] = [
      'categories', 'suppliers', 'grns', 'expenses'
    ]
    return allCollections.filter(c => this.isStale(c))
  }

  /**
   * Subscribe to sync events for a specific collection.
   * Returns an unsubscribe function.
   */
  on(collection: SyncCollection, handler: SyncHandler): () => void {
    if (!this.handlers.has(collection)) {
      this.handlers.set(collection, new Set())
    }
    this.handlers.get(collection)!.add(handler)

    return () => {
      this.handlers.get(collection)?.delete(handler)
    }
  }

  /**
   * Subscribe to ALL sync events (any collection).
   * Returns an unsubscribe function.
   */
  onAny(handler: SyncHandler): () => void {
    this.globalHandlers.add(handler)
    return () => {
      this.globalHandlers.delete(handler)
    }
  }

  /**
   * Handle incoming messages from other tabs.
   */
  private handleIncomingMessage(message: SyncMessage) {
    // Ignore messages from our own tab (BroadcastChannel shouldn't send to self, but just in case)
    if (message.sourceTabId === this.tabId) return

    console.log(`[LiveSync] Received: ${message.action} on ${message.collection} from ${message.sourceTabId}`)

    // Notify collection-specific handlers
    const collectionHandlers = this.handlers.get(message.collection)
    if (collectionHandlers) {
      collectionHandlers.forEach(handler => {
        try {
          handler(message)
        } catch (e) {
          console.error('[LiveSync] Handler error:', e)
        }
      })
    }

    // Notify global handlers
    this.globalHandlers.forEach(handler => {
      try {
        handler(message)
      } catch (e) {
        console.error('[LiveSync] Global handler error:', e)
      }
    })
  }

  /**
   * Handle tab becoming visible - refresh stale data.
   */
  private handleTabBecameVisible() {
    const staleCollections = this.getStaleCollections()
    if (staleCollections.length > 0) {
      console.log('[LiveSync] Tab visible, stale collections:', staleCollections)
      if (this.onVisibilityRefresh) {
        this.onVisibilityRefresh()
      }
    }
  }

  /**
   * Get the unique tab ID for debugging.
   */
  getTabId(): string {
    return this.tabId
  }
}

// Singleton instance
export const liveSyncService = new LiveSyncService()
